/**
 * Biomes — the "biome" trait, and the only thing that decides what the *land*
 * looks like.
 *
 * A biome is a parameter set for the noise field plus two soft preferences. It is
 * deliberately not a colour scheme: palette and biome are independent traits, so
 * Karst can be Frost or Ember and both read as the same landform. What a biome
 * controls is shape — frequency, roughness, whether crests are folded into ridges,
 * how much of the frame drops below water, and how hard elevation is pushed toward
 * its extremes.
 *
 * Every field is an integer in the units the engine consumes, so a biome can be
 * handed to `terrain.ts` with no conversion and mirrored into Solidity as a
 * `struct` of `uint32`s.
 *
 * The two preferences (`favours`, `charsets`) bias the palette and glyph-set draws
 * rather than fixing them. Boosting by name keeps the data readable — a per-biome
 * vector of sixteen palette weights would be unreviewable and would silently rot
 * the moment a palette is added. `validateBiomes()` fails the build if a named
 * palette or glyph set does not exist, which is what makes name-based safe.
 */

import { HALF, ONE } from "../constants.js";
import { GLYPH_SETS } from "./glyphs.js";
import { PALETTES } from "./palettes.js";

export interface Biome {
  readonly name: string;

  /**
   * Noise units advanced per grid cell, Q16.16. One noise unit is one lattice
   * cell, so `frequency * GRID_COLS / ONE` is roughly how many features span the
   * canvas. 8192 (= 0.125) gives four across a 32-cell grid.
   */
  readonly frequency: number;
  /** fBm octaves, 1..6. Each one doubles on-chain cost. */
  readonly octaves: number;
  /** Amplitude falloff per octave, Q16.16. `HALF` is the neutral choice. */
  readonly gain: number;
  /** Frequency growth per octave, Q16.16. `2 * ONE` is the neutral choice. */
  readonly lacunarity: number;

  /**
   * Domain-warp strength in lattice cells, Q16.16. 0 disables warping and saves
   * two noise evaluations per cell. Must not exceed `8 * ONE`.
   */
  readonly warp: number;
  /** Blend toward the ridged field, in 1/256. 0 = pure fBm, 256 = pure ridged. */
  readonly ridge: number;
  /**
   * Elevation contrast about the midpoint, in 1/256. 256 leaves the field alone;
   * above that pushes toward the extremes (cliffs and plateaus), below flattens
   * toward mid ground.
   */
  readonly relief: number;
  /**
   * Height below which a cell is water, in the noise field's own 0..65535 units.
   * 0 means the biome has no water at all.
   */
  readonly waterLevel: number;
  /**
   * Radial falloff from the canvas centre outward, signed, in 1/256.
   *
   * Positive pulls the *corners* down, which is what frames the bird: concentrating
   * high ground in the middle third means the creature is never fighting a busy edge.
   * Negative pulls the *centre* down instead, hollowing the frame into a bowl. 0
   * disables it.
   *
   * The sign exists because Basin's authored intent — "the middle is the low point" —
   * was unreachable with an unsigned falloff. It was described in a comment and
   * implemented nowhere, so the biome was quietly just a low-contrast Highland with
   * water. Either the comment or the parameter had to change; the bowl is the better
   * biome, so the parameter did.
   */
  readonly vignette: number;

  /** Palettes this biome pulls toward, by name. Empty means no preference. */
  readonly favours: readonly string[];
  /** Glyph sets this biome pulls toward, by name. Empty means no preference. */
  readonly charsets: readonly string[];

  readonly weight: number;
}

/**
 * Weight multipliers a biome applies to its trait draws, in 1/16.
 *
 * A one-sided boost was tried first and measurably failed: Karst favours Cinder,
 * Salt and Marrow, which are three of the four *rarest* palettes, so multiplying
 * them by 2.5 still left Loam (base weight 14) at 10.0% against Cinder's 10.7% —
 * the biome's stated preference was not visible in the distribution at all.
 * Damping the unfavoured side is what makes a preference actually preferential:
 * with both multipliers Karst's chosen trio takes ~40% of its tokens and Loam
 * drops to ~8%, while every palette still appears in every biome.
 */
export const FAVOUR_BOOST = 40;
export const FAVOUR_DAMP = 9;

export const BIOMES: readonly Biome[] = [
  {
    // The default reading of the field: rolling ground, nothing exotic. Deliberately
    // the most common, because it is the biome the creature reads best against.
    name: "Highland",
    frequency: 7000,
    octaves: 4,
    gain: HALF,
    lacunarity: 2 * ONE,
    warp: 0,
    ridge: 0,
    relief: 288,
    waterLevel: 0,
    vignette: 96,
    favours: ["Loam", "Rust", "Terracotta"],
    charsets: ["Strata", "Column"],
    weight: 16,
  },
  {
    // High water, low land: the bird stands on one of a handful of islands, which is
    // the strongest framing in the collection. The water level is not the authored
    // 30000 it started at — once `radialFalloff` was fixed and the vignette ran at
    // full strength, 30000 drowned 72% of the frame and left nowhere to stand.
    // 22000 measures 41% water over eight seeds, which is islands.
    name: "Archipelago",
    frequency: 5200,
    octaves: 5,
    gain: HALF,
    lacunarity: 2 * ONE,
    warp: ONE / 2,
    ridge: 0,
    relief: 320,
    waterLevel: 22000,
    vignette: 160,
    favours: ["Abyss", "Frost", "Aurora"],
    charsets: ["Strata", "Braille"],
    weight: 11,
  },
  {
    // Wind. Few octaves so the shapes stay large, heavy warp so they flow.
    name: "Dunes",
    frequency: 4200,
    octaves: 2,
    gain: 40000,
    lacunarity: 2 * ONE,
    warp: (ONE * 3) / 2,
    ridge: 64,
    relief: 224,
    waterLevel: 0,
    vignette: 64,
    favours: ["Terracotta", "Bone", "Sodium"],
    charsets: ["Column", "Cursive"],
    weight: 12,
  },
  {
    // Limestone spires. Almost pure ridged, high contrast, no water: vertical.
    name: "Karst",
    frequency: 9000,
    octaves: 5,
    gain: 30000,
    lacunarity: 2 * ONE + 8192,
    warp: ONE / 4,
    ridge: 224,
    relief: 352,
    waterLevel: 0,
    vignette: 112,
    favours: ["Cinder", "Salt", "Marrow"],
    charsets: ["Circuit", "Mosaic"],
    weight: 9,
  },
  {
    // Inverted relief: the middle is the *low* point, which is what the negative
    // vignette does. The only biome where the bird stands in a bowl, so its water
    // pools in the centre of the frame rather than around the edges — and the one
    // place a light palette reads as a salt flat.
    name: "Basin",
    frequency: 6000,
    octaves: 3,
    gain: HALF,
    lacunarity: 2 * ONE,
    warp: 0,
    ridge: 0,
    relief: 208,
    waterLevel: 14000,
    vignette: -128,
    favours: ["Salt", "Bone", "Cinder"],
    charsets: ["Strata", "Teletype"],
    weight: 8,
  },
  {
    // Cracked ground. Ridged plus the heaviest warp in the set, which turns the
    // crests into branching fissures rather than parallel combs.
    name: "Fissure",
    frequency: 11000,
    octaves: 4,
    gain: 34000,
    lacunarity: 2 * ONE + 16384,
    warp: 2 * ONE,
    ridge: 256,
    relief: 384,
    waterLevel: 0,
    vignette: 128,
    favours: ["Ember", "Ultraviolet", "Signal"],
    charsets: ["Circuit", "Hexcode"],
    weight: 8,
  },
  {
    // Almost flat. `relief: 160` compresses every height toward the midpoint, which
    // measures four of eight bands and makes the bird the only detail in the frame.
    // That compression is also why the water line sits so high: it puts the field's
    // floor near 12000, so the authored 6000 was unreachable and the biome rendered
    // bone dry. 20000 measures 6% — a shallow pan, not a sea.
    // Rare, because emptiness is only interesting occasionally.
    name: "Tundra",
    frequency: 2600,
    octaves: 2,
    gain: 22000,
    lacunarity: 2 * ONE,
    warp: 0,
    ridge: 0,
    relief: 160,
    waterLevel: 20000,
    vignette: 48,
    favours: ["Frost", "Bone", "Cinder"],
    charsets: ["Teletype", "Cursive"],
    weight: 7,
  },
  {
    // Overgrowth seen from above. Highest frequency and octave count, so the ground
    // is texture rather than landform.
    name: "Canopy",
    frequency: 15000,
    octaves: 6,
    gain: 38000,
    lacunarity: 2 * ONE,
    warp: ONE,
    ridge: 96,
    relief: 272,
    waterLevel: 0,
    vignette: 144,
    favours: ["Chlorophyll", "Verdigris", "Signal"],
    charsets: ["Spores", "Braille"],
    weight: 10,
  },
  {
    // Terraced sediment. Low warp and moderate ridge, but the highest relief in the
    // set, so the quantiser lands on wide flat steps with hard edges between them.
    name: "Strata",
    frequency: 6400,
    octaves: 3,
    gain: 44000,
    lacunarity: 2 * ONE,
    warp: ONE / 8,
    ridge: 32,
    relief: 416,
    waterLevel: 0,
    vignette: 80,
    favours: ["Rust", "Loam", "Marrow"],
    charsets: ["Strata", "Column", "Mosaic"],
    weight: 9,
  },
] as const;

export const BIOME_WEIGHTS: readonly number[] = BIOMES.map((b) => b.weight);

/**
 * Apply a biome's preference to a weight vector, by name.
 *
 * Returns a fresh array; the caller feeds it to `Rng.weighted`. Favoured entries are
 * boosted and the rest are damped, never removed — the floor of 1 guarantees that
 * every palette and every glyph set remains reachable from every biome, which is
 * what keeps the collection from looking like nine separate collections.
 */
export function biasWeights(
  base: readonly number[],
  names: readonly string[],
  favoured: readonly string[],
): number[] {
  if (favoured.length === 0) return [...base];
  const out: number[] = [];
  for (let i = 0; i < base.length; i++) {
    const w = base[i] as number;
    const mul = favoured.includes(names[i] as string) ? FAVOUR_BOOST : FAVOUR_DAMP;
    out.push(Math.max(1, (w * mul) >> 4));
  }
  return out;
}

/** Structural guard invoked by tests. Every named reference must resolve. */
export function validateBiomes(): void {
  const paletteNames = new Set(PALETTES.map((p) => p.name));
  const charsetNames = new Set(GLYPH_SETS.map((g) => g.name));
  const seen = new Set<string>();

  for (const b of BIOMES) {
    if (seen.has(b.name)) throw new Error(`duplicate biome: ${b.name}`);
    seen.add(b.name);

    if (b.weight <= 0) throw new Error(`${b.name}: weight must be positive`);
    if (b.octaves < 1 || b.octaves > 6) throw new Error(`${b.name}: octaves out of range`);
    if (b.gain <= 0 || b.gain >= ONE) throw new Error(`${b.name}: gain must be in (0, 1)`);
    if (b.lacunarity <= ONE) throw new Error(`${b.name}: lacunarity must exceed 1.0`);
    if (b.frequency <= 0) throw new Error(`${b.name}: frequency must be positive`);
    if (b.warp < 0 || b.warp > 8 * ONE) throw new Error(`${b.name}: warp exceeds noise limit`);
    if (b.ridge < 0 || b.ridge > 256) throw new Error(`${b.name}: ridge must be in [0, 256]`);
    if (b.relief <= 0 || b.relief > 512) throw new Error(`${b.name}: relief must be in (0, 512]`);
    if (b.waterLevel < 0 || b.waterLevel > 65535) throw new Error(`${b.name}: bad waterLevel`);
    if (b.vignette < -256 || b.vignette > 256) {
      throw new Error(`${b.name}: vignette must be in [-256, 256]`);
    }

    for (const n of b.favours) {
      if (!paletteNames.has(n)) throw new Error(`${b.name}: favours unknown palette "${n}"`);
    }
    for (const n of b.charsets) {
      if (!charsetNames.has(n)) throw new Error(`${b.name}: favours unknown glyph set "${n}"`);
    }
    if (new Set(b.favours).size !== b.favours.length) throw new Error(`${b.name}: duplicate favour`);
    if (new Set(b.charsets).size !== b.charsets.length) throw new Error(`${b.name}: duplicate charset`);
  }
}
